import { useEffect, useRef } from 'react';

import { ChatMentionSuggestion, PromptChatAdapter } from '../../../components/Chat/PromptChatAdapter';
import { designerChatAgentIds, designerChatScopes, useDesignerChatAdapter } from '../../../components/Chat/useDesignerChatAdapter';
import useFlowMetadata from './Metadata/useFlowMetadata';
import { FlowNode } from '../Models/GraphModel';

export type FlowDesignerChatContext = {
	currentActivityId?: string;
	currentTransitionId?: string;
	nodes?: FlowNode[];
};

export function useFlowDesignerChatAdapter(flowDefinitionId: string | undefined, context?: FlowDesignerChatContext): PromptChatAdapter {
	const { activityByName } = useFlowMetadata();
	const contextRef = useRef(context);
	const activityByNameRef = useRef(activityByName);

	contextRef.current = context;

	useEffect(() => {
		activityByNameRef.current = activityByName;
	}, [activityByName]);

	return useDesignerChatAdapter({
		scopeName: designerChatScopes.flow,
		agentId: designerChatAgentIds.flow,
		targetId: flowDefinitionId,
		missingTargetMessage: 'Save the flow before starting a chat.',
		messageAttributes: () => ({
			CurrentActivityId: contextRef.current?.currentActivityId,
			CurrentTransitionId: contextRef.current?.currentTransitionId,
		}),
		searchMentions: async (query: string): Promise<ChatMentionSuggestion[]> => {
					const q = query.toLowerCase();
					const suggestions: ChatMentionSuggestion[] = [];
					const seen = new Set<string>();

					for (const node of contextRef.current?.nodes ?? []) {
						const metadata = activityByNameRef.current.get(node.data.Type);
						const label = String(node.data.Metadata?.Name ?? metadata?.displayName ?? node.data.Type ?? node.id);
						if (label.toLowerCase().includes(q) && !seen.has(label)) {
							seen.add(label);
							suggestions.push({ name: label, type: 'Activity' });
						}
					}

					for (const activity of Array.from(activityByNameRef.current.values())) {
						const label = activity.displayName || activity.name;
						if (label.toLowerCase().includes(q) && !seen.has(label)) {
							seen.add(label);
							suggestions.push({ name: label, type: 'ActivityType' });
						}
					}

					return suggestions;
		},
	});
}
